import { useState } from "react";
import { Loader2, Plus, Trash2, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatError } from "@/lib/api";

const blank = () => ({ id: null, title: "", duration: "", video_url: "" });

function LessonRow({ row, i, n, onChange, onRemove, cid }) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center" data-testid={`lesson-row-${cid}-${i}`}>
      <span className="w-6 text-xs font-bold text-slate-400 shrink-0">{n}.</span>
      <Input value={row.title} onChange={(e) => onChange({ ...row, title: e.target.value })} placeholder="Lesson name"
        className="sm:flex-[1.2] min-w-0" data-testid={`lesson-title-${cid}-${i}`} />
      <Input value={row.duration || ""} onChange={(e) => onChange({ ...row, duration: e.target.value })} placeholder="3:26"
        className="sm:w-20 shrink-0" data-testid={`lesson-duration-${cid}-${i}`} />
      <Input value={row.video_url} onChange={(e) => onChange({ ...row, video_url: e.target.value })} placeholder="https://youtube.com/… (empty = coming soon)"
        className="flex-1 min-w-0" data-testid={`lesson-url-${cid}-${i}`} />
      {row.video_url && (
        <a href={row.video_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-red-600 shrink-0" title="Open link"><ExternalLink size={16} /></a>
      )}
      <button onClick={onRemove} className="text-slate-400 hover:text-red-600 shrink-0" title="Remove lesson" data-testid={`lesson-remove-${cid}-${i}`}><Trash2 size={16} /></button>
    </div>
  );
}

export default function LessonEditor({ grade, subject, chapterId, initial, onSaved, start = 0 }) {
  const [rows, setRows] = useState(initial.length ? initial : [blank()]);
  const [saving, setSaving] = useState(false);
  const set = (i, r) => setRows((rs) => rs.map((x, j) => (j === i ? r : x)));

  const save = async () => {
    const lessons = rows.filter((r) => r.title.trim()).map(({ id, title, duration, video_url }) => ({ id, title, duration: (duration || "").trim(), video_url: video_url || "" }));
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/lessons/${encodeURIComponent(chapterId)}`, { grade: Number(grade), subject, lessons });
      setRows(data.lessons.length ? data.lessons : [blank()]);
      onSaved(data.lessons);
      toast.success(`Saved ${data.lessons.length} lesson(s)`);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid={`lesson-editor-${chapterId}`}>
      {rows.map((r, i) => (
        <LessonRow key={r.id || `new-${i}`} row={r} i={i} n={start + i + 1} cid={chapterId} onChange={(nr) => set(i, nr)}
          onRemove={() => setRows((rs) => rs.filter((_, j) => j !== i))} />
      ))}
      <div className="flex gap-2 pt-1">
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => setRows((rs) => [...rs, blank()])} data-testid={`lesson-add-${chapterId}`}>
          <Plus size={14} className="mr-1" /> Add lesson
        </Button>
        <Button size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800 ml-auto" onClick={save} disabled={saving} data-testid={`lesson-save-${chapterId}`}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} className="mr-1" /> Save lessons</>}
        </Button>
      </div>
    </div>
  );
}
